import { createAsyncThunk } from '@reduxjs/toolkit';
import { PaymentMethod } from '@wix/cashier-common/dist/src/enums/payments/PaymentMethod';
import { PaymentProvider } from '@wix/cashier-common/dist/src/enums/payments/PaymentProvider';
import { t } from '../../utils/i18n';
import { type BiLogger } from '../../components/Provider/BiLogger/BiLogger';
import { createGooglePayPayment } from '../../utils/serverAPI/createPayment';
import { logGeneralError } from '../../utils/sentry';
import {
  selectAuthParams,
  selectGooglePayCountryCode,
  selectGooglePayPaymentData,
  selectSessionId,
} from '../../selectors';
import {
  getUnifiedBillingContact,
  getUnifiedShippingContact,
} from '../../components/payment-methods/GooglePay/utils/getUnifiedContactData';
import { type StoreState } from '../../types/StoreState';
import { type BillingContact } from '../../types/BillingContact';
import { type ShippingContact } from '../../types/Shipping';
import { InteractionName } from '../../types/InteractionName';
import { type WidgetFedopsLogger } from '../../components/Provider/Fedops/FedopsProvider';
import { getInitialTransactionInfo } from '../../components/payment-methods/GooglePay/utils/getInitialPaymentData';
import { spbButtonTrxAuthorized } from '../../utils/bi/events/biEvents';
import { setGooglePayPaymentDataShippingMethods } from './common';

type OnPaymentAuthorizedParams = {
  transactionInfo: google.payments.api.TransactionInfo;
  biLogger: BiLogger;
  fedopsLogger: WidgetFedopsLogger;
  paymentData: google.payments.api.PaymentData;
};

type OnPaymentAuthorizedReturn = google.payments.api.PaymentAuthorizationResult;

export const onPaymentAuthorized = createAsyncThunk<
  OnPaymentAuthorizedReturn,
  OnPaymentAuthorizedParams,
  {
    state: StoreState;
  }
>('GOOGLE_PAYMENT_AUTHORIZED', async ({ biLogger, fedopsLogger, paymentData }, { dispatch, getState }) => {
  const store = getState();
  const { topProps } = store;
  const auth = selectAuthParams(store);
  const sessionId = selectSessionId(store);
  const { shippingMethods } = selectGooglePayPaymentData(store);
  const countryCode = selectGooglePayCountryCode(store);

  let detailsId: string;

  // Google didn't call "INITIALIZE" event when [requestShipping:false] (digital product)
  if (!topProps.requestShipping) {
    const transactionInfo = await getInitialTransactionInfo({
      paymentAmount: topProps.paymentAmount,
      paymentBreakdown: topProps.paymentBreakdown,
      currency: topProps.currency,
      countryCode,
      sessionId,
      getInitialPaymentData: topProps.getInitialPaymentData,
      biLogger,
      requestShipping: topProps.requestShipping,
    });
    if (transactionInfo.error) {
      return {
        transactionState: 'ERROR' as const,
        error: transactionInfo.error,
      };
    }
  }

  biLogger.report(spbButtonTrxAuthorized, {
    paymentmethod: PaymentMethod.GooglePay,
    paymentProvider: PaymentProvider.WixPayUS,
    currency: undefined as any, // TODO[bi]
    isPartner: undefined as any, // TODO[bi]
    amount: undefined as any, // TODO[bi]
    externalOrderId: undefined as any, // TODO[bi]
    cartId: undefined as any, // TODO[bi]
    orderSnapshotId: undefined as any, // TODO[bi]
    transactionId: undefined as any, // TODO[bi]
  });

  try {
    try {
      fedopsLogger.interactionStarted(InteractionName.GooglePayOnPaymentAuthorizedLoadData);
      detailsId = await createGooglePayPayment(paymentData, auth, sessionId);
      fedopsLogger.interactionEnded(InteractionName.GooglePayOnPaymentAuthorizedLoadData);
    } catch (error) {
      await topProps.onPaymentAuthorized({
        error,
      });
      return {
        transactionState: 'ERROR' as const,
        error: {
          reason: 'OTHER_ERROR',
          intent: 'PAYMENT_AUTHORIZATION',
          message: t('pm.onPaymentAuthorized.unknownError'),
        },
      };
    }

    // Note this billing address will only be populated when billing address is set as required through [CardParameters.billingAddressRequired]
    const billing = paymentData.paymentMethodData.info!.billingAddress as google.payments.api.Address;
    const shippingContact = paymentData.shippingAddress;
    const billingContactUnified: BillingContact = getUnifiedBillingContact({ email: paymentData.email, billing });
    const shippingContactUnified: ShippingContact | undefined = shippingContact
      ? getUnifiedShippingContact(shippingContact)
      : undefined;
    const selectedMethod = Array.isArray(shippingMethods)
      ? shippingMethods.find((method) => method.identifier === paymentData.shippingOptionData.id)
      : null;

    const response = await topProps.onPaymentAuthorized({
      detailsId,
      billingContact: billingContactUnified,
      ...(shippingContactUnified && { shippingContact: shippingContactUnified }),
      ...(selectedMethod && { shippingMethods: [selectedMethod] }),
    });

    if (response.result === 'success') {
      dispatch(setGooglePayPaymentDataShippingMethods(topProps.shippingMethods)); // reset state to initial value

      return {
        transactionState: 'SUCCESS' as const,
      };
    }

    return {
      transactionState: 'ERROR' as const,
      error: {
        reason: 'OTHER_ERROR',
        intent: 'PAYMENT_AUTHORIZATION',
        message: t('pm.onPaymentAuthorized.baseKnownError'),
      },
    };
  } catch (error) {
    logGeneralError({
      message: '[GooglePay] paymentAuthorizeHandler failed',
      payload: error,
    });
    return {
      transactionState: 'ERROR' as const,
      error: {
        reason: 'OTHER_ERROR',
        intent: 'PAYMENT_AUTHORIZATION',
        message: t('pm.onPaymentAuthorized.unknownError'),
      },
    };
  }
});
